/**
 * Minifigs 2.0 - Cart Remove Handler with BOLD Product Options support
 *
 * Intercepts cart remove button clicks and handles removal of BOLD shadow variants
 * (hidden option products) alongside the main product to prevent orphaned charges.
 *
 * @author Matt Haynes
 * @package Joi Polloi
 */

const cartRemoveHandler = () => {
    let isProcessing = false;

    /**
     * Handle cart remove button clicks at capture phase (before Dawn's custom element)
     */
    const handleRemoveClick = async (event) => {
        const removeButton = event.target.closest('cart-remove-button');
        if (!removeButton) return;

        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (isProcessing) {
            return;
        }

        if (!window.confirm('Are you sure you want to remove this item from your cart?')) {
            return;
        }

        isProcessing = true;

        try {
            // Get the cart item key from data attribute
            const itemKey = removeButton.dataset.key;
            if (!itemKey) {
                console.error('[Cart Remove] No item key found on remove button');
                throw new Error('Missing item key');
            }

            console.log('[Cart Remove] Removing item with key:', itemKey);

            // Fetch current cart state
            const cartResponse = await fetch('/cart.js');
            if (!cartResponse.ok) {
                throw new Error('Failed to fetch cart');
            }
            const cart = await cartResponse.json();

            // Find the item being removed
            const itemToRemove = cart.items.find((item) => item.key === itemKey);
            if (!itemToRemove) {
                console.error('[Cart Remove] Item not found in cart:', itemKey);
                throw new Error('Item not found in cart');
            }

            console.log('[Cart Remove] Found item:', itemToRemove.title);

            // Collect all items to remove (main item + BOLD shadow variants)
            const itemsToRemove = [itemToRemove];

            // If the item has a BOLD builder ID, find associated shadow variants
            if (itemToRemove.properties && itemToRemove.properties._boldBuilderId) {
                const boldBuilderId = itemToRemove.properties._boldBuilderId;
                console.log('[Cart Remove] Item has BOLD builder ID:', boldBuilderId);

                // Find BOLD shadow variants (items with matching _boldBuilderId but no _boldVariantIds)
                const shadowVariants = cart.items.filter((item) => {
                    return (
                        item.properties &&
                        item.properties._boldBuilderId === boldBuilderId &&
                        (item.properties._boldVariantIds === null || item.properties._boldVariantIds === undefined)
                    );
                });

                if (shadowVariants.length > 0) {
                    console.log('[Cart Remove] Found', shadowVariants.length, 'BOLD shadow variant(s)');
                    itemsToRemove.push(...shadowVariants);
                }
            }

            console.log('[Cart Remove] Removing', itemsToRemove.length, 'item(s) total');

            // Remove all items by setting quantity to 0
            for (const item of itemsToRemove) {
                console.log('[Cart Remove] Removing:', item.title || item.key);
                await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        id: item.key,
                        quantity: 0,
                    }),
                });
            }

            console.log('[Cart Remove] Successfully removed all items');

            // Trigger Dawn theme's cart refresh via AJAX
            await refreshCart(removeButton);
        } catch (error) {
            console.error('[Cart Remove] Error during removal:', error);

            // Fallback, hopefully never triggered: try to remove just the primary item - look here if BOLD items remain while main item is removed
            try {
                const itemKey = removeButton.dataset.key;
                if (itemKey) {
                    console.log('[Cart Remove] Fallback: removing primary item only');
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            id: itemKey,
                            quantity: 0,
                        }),
                    });
                    await refreshCart(removeButton);
                }
            } catch (fallbackError) {
                console.error('[Cart Remove] Fallback removal also failed:', fallbackError);
                alert('Failed to remove item from cart. Please refresh the page and try again.');
            }
        } finally {
            isProcessing = false;
        }
    };

    /**
     * Trigger Dawn theme's cart refresh using its built-in mechanism
     */
    const refreshCart = async (removeButton) => {
        const cartItems = removeButton.closest('cart-items');
        const cartDrawerItems = removeButton.closest('cart-drawer-items');
        const container = cartItems || cartDrawerItems;

        if (!container) {
            console.error('[Cart Remove] Could not find cart container for refresh');
            // Fallback to page reload
            window.location.reload();
            return;
        }

        // Check if the container has a renderContents method (Dawn theme pattern)
        if (typeof container.renderContents === 'function') {
            console.log('[Cart Remove] Using Dawn renderContents method');

            // Fetch updated cart HTML sections
            const sectionsToRender = container.getSectionsToRender
                ? container.getSectionsToRender()
                : getSectionsToRender();

            const sections = sectionsToRender.map((section) => section.section);

            const response = await fetch(`${window.Shopify.routes.root}?sections=${sections.join(',')}`);
            const html = await response.text();
            const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

            container.renderContents(parsedHTML);
        } else {
            // Fallback: reload the page
            console.log('[Cart Remove] No renderContents method, reloading page');
            window.location.reload();
        }
    };

    /**
     * Get sections to render (Dawn theme pattern)
     */
    const getSectionsToRender = () => {
        return [
            {
                id: 'main-cart-items',
                section: document.getElementById('main-cart-items')?.dataset.id,
                selector: '.js-contents',
            },
            {
                id: 'cart-icon-bubble',
                section: 'cart-icon-bubble',
                selector: '.shopify-section',
            },
            {
                id: 'cart-live-region-text',
                section: 'cart-live-region-text',
                selector: '.shopify-section',
            },
            {
                id: 'main-cart-footer',
                section: document.getElementById('main-cart-footer')?.dataset.id,
                selector: '.js-contents',
            },
        ].filter((section) => section.section);
    };

    // Attach event listener at capture phase to intercept before Dawn's custom element
    document.addEventListener('click', handleRemoveClick, true);

    console.log('[Cart Remove Handler] Initialized');
};

export default cartRemoveHandler;
